import { renderAvatarSVG, sanitizeAvatar } from "./AvatarBuilder.js";
import { formatDuration } from "./TimeUtils.js";
import { VISIBLE_GROUPS } from "./gameGroups.js";
import { PICTO_FLAME, PICTO_CLOCK, PICTO_CROSS, PICTO_CHART, medalPicto } from "./Pictos.js";

/**
 * Rendu du mini profil joueur (avatar, pseudo, stats par jeu), partagé entre
 * la popup publique du classement (`Leaderboard.js`) et celle du back-office
 * (`Admin.js`) : mêmes classes CSS (`.player-profile-*`, définies dans
 * `_leaderboard.scss` mais globales au bundle), pour que les deux ne
 * divergent jamais visuellement. Le back-office y ajoute ses propres blocs
 * (email, badges, actions) autour de ce que cette fonction rend.
 */

export function rankBadge(rank) {
    return medalPicto(rank) ?? rank;
}

export function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

/** L'avatar est une config JSON stockée telle quelle en base ; null pour les comptes sans avatar personnalisé. */
export function avatarHtml(entry, size) {
    let config = null;
    try { config = entry.avatar ? JSON.parse(entry.avatar) : null; } catch { /* avatar invalide, on retombe sur le défaut */ }
    return renderAvatarSVG(sanitizeAvatar(config), size);
}

const HEART_PATH = "m259.17 100.8c-65.176 0-105.17 56.996-105.17 103.75 0 101.09 114.9 194.92 196 254.65 81.102-59.734 196-153.57 196-254.65 0-46.75-39.996-103.75-105.17-103.75-36.398 0-67.496 29.02-90.828 56.59-23.332-27.57-54.43-56.59-90.828-56.59z";

function heartSvg(filled) {
    const fill = filled ? "red" : "rgba(255, 255, 255, 0.2)";
    const stroke = filled ? "red" : "rgba(255, 255, 255, 0.4)";
    return `<svg class="lb-heart" viewBox="150 95 400 370" xmlns="http://www.w3.org/2000/svg"><path d="${HEART_PATH}" fill="${fill}" stroke="${stroke}" stroke-width="10" /></svg>`;
}

/**
 * Les cœurs sont des SVG décoratifs : le nombre de vies restantes n'existe que
 * visuellement. Le groupe porte donc le libellé accessible, sinon un lecteur
 * d'écran annonce une cellule vide.
 */
export function renderHearts(errors, label) {
    if (errors === null || errors === undefined) return "–";
    const remaining = Math.max(0, 3 - errors);
    const hearts = Array.from({ length: 3 }, (_, i) => heartSvg(i < remaining)).join("");
    return `<span class="lb-hearts" role="img" aria-label="${escapeHtml(label(remaining))}">${hearts}</span>`;
}

/** Date d'inscription, en absolu (contrairement à une fraîcheur relative) : elle ne change plus une fois passée. */
export function formatMemberSince(timestamp, l) {
    if (!timestamp) return "";
    const locale = l.currentLang === "en" ? "en-US" : "fr-FR";
    const date = new Date(timestamp).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    return l.t("leaderboard.profileMemberSince").replace("{date}", date);
}

export function renderProfileHeader({ avatar, username, createdAt }, l) {
    const memberSince = formatMemberSince(createdAt, l);
    return `
        <div class="player-profile-header">
            <span class="player-profile-avatar">${avatarHtml({ avatar }, 64)}</span>
            <h2 class="player-profile-name">${escapeHtml(username)}</h2>
            ${memberSince ? `<p class="player-profile-since">${memberSince}</p>` : ""}
        </div>
    `;
}

/** Le rang n'existe que côté classement public (calculé face à tous les joueurs) : absent, il ne s'affiche simplement pas. */
function rankText(rank) {
    if (rank == null) return "";
    return rank <= 3 ? rankBadge(rank) : `#${rank}`;
}

/**
 * `null` si le joueur n'a jamais fini dans le top 3 d'un jour : rien à
 * afficher plutôt qu'un "🥇0 🥈0 🥉0" qui alourdit chaque ligne. Un span par
 * médaille (pas juste du texte) pour que chacune porte sa couleur — l'or ne
 * doit pas se perdre dans le gris discret du reste des détails d'un mode.
 */
function formatMedals(medals) {
    if (!medals) return "";
    return [
        medals.gold ? `<span class="player-profile-medal player-profile-medal--gold">${medalPicto(1)} ${medals.gold}</span>` : "",
        medals.silver ? `<span class="player-profile-medal player-profile-medal--silver">${medalPicto(2)} ${medals.silver}</span>` : "",
        medals.bronze ? `<span class="player-profile-medal player-profile-medal--bronze">${medalPicto(3)} ${medals.bronze}</span>` : "",
    ].filter(Boolean).join("");
}

/** Bloc palmarès complet (titre + médailles), centré et mis en avant — un trophée, pas une statistique de plus. */
function renderMedalsBlock(medals, l) {
    const medalsHtml = formatMedals(medals);
    if (!medalsHtml) return "";
    return `<div class="player-profile-stat-medals">
        <span class="player-profile-stat-medals-title">${l.t("leaderboard.dailyMedalsTitle")}</span>
        <div class="player-profile-stat-medals-list">${medalsHtml}</div>
    </div>`;
}

/**
 * Un encart par statistique secondaire (erreurs, temps, moyenne, parties…)
 * plutôt qu'une seule ligne de texte séparée par des « · » : ça se lisait
 * comme un flux brut, surtout avec 3-4 valeurs bout à bout. Chaque encart
 * (bordure, fond) se repère d'un coup d'œil et passe à la ligne suivante de
 * lui-même sur une largeur de popup étroite.
 */
function renderChips(chips) {
    if (!chips.length) return "";
    return `<div class="player-profile-chips">${chips.map(c => `<span class="player-profile-chip">${c}</span>`).join("")}</div>`;
}

/** Une ligne de mode : le nom du jeu vit déjà dans l'en-tête du groupe (ou du score global), inutile de le répéter à chaque mode. */
function renderStatRow(mode, e, l, medals) {
    const scoreText = mode.total ? `${e.score}<span class="lb-total">/${mode.total}</span>` : e.score;
    // Les modes à vies (Classic/Hard) montrent les cœurs restants, pas un
    // nombre d'erreurs brut — c'est ce que le joueur voit pendant la partie.
    const errorsChip = e.errors == null || mode.type === "letters" ? ""
        : mode.type === "classic" ? renderHearts(e.errors, n => l.t("leaderboard.livesLeft").replace("{n}", n))
        : `${PICTO_CROSS}${e.errors} ${l.t("leaderboard.errors").toLowerCase()}`;
    const chips = [
        mode.type === "daily" && e.streak ? `${PICTO_FLAME}${e.streak}` : "",
        errorsChip,
        e.duration != null ? `${PICTO_CLOCK}${formatDuration(e.duration)}` : "",
        mode.type === "letters" && e.found != null ? `${e.found} ${l.t("leaderboard.countriesFound").toLowerCase()}` : "",
        e.avgScore != null ? `${PICTO_CHART}${l.t("leaderboard.avgScore")} ${e.avgScore}` : "",
        e.gamesPlayed ? l.t("leaderboard.profileGamesPlayed").replace("{n}", e.gamesPlayed) : "",
    ].filter(Boolean);

    return `<li class="player-profile-stat">
        <span class="player-profile-stat-name">${l.t(mode.modeKey)}</span>
        <span class="player-profile-stat-rank">${rankText(e.rank)}</span>
        <span class="player-profile-stat-score">${scoreText}</span>
        ${renderChips(chips)}
        ${renderMedalsBlock(medals, l)}
    </li>`;
}

/**
 * Le Défi quotidien n'a de score « aujourd'hui » que si le joueur a joué
 * aujourd'hui : sans lui, ni le palmarès or/argent/bronze ni la progression
 * cumulée (moyenne, parties jouées, série) — bien réels, gagnés les jours
 * précédents — ne devraient rester invisibles. Cette ligne réduite (pas de
 * score du jour, pas de rang) porte ce qu'il reste à montrer pour ce mode.
 */
function renderFallbackRow(mode, fallback, medals, l) {
    const chips = fallback ? [
        fallback.streak ? `${PICTO_FLAME}${fallback.streak}` : "",
        fallback.avgScore != null ? `${PICTO_CHART}${l.t("leaderboard.avgScore")} ${fallback.avgScore}` : "",
        fallback.gamesPlayed ? l.t("leaderboard.profileGamesPlayed").replace("{n}", fallback.gamesPlayed) : "",
    ].filter(Boolean) : [];

    return `<li class="player-profile-stat">
        <span class="player-profile-stat-name">${l.t(mode.modeKey)}</span>
        <span class="player-profile-stat-rank"></span>
        ${renderChips(chips)}
        ${renderMedalsBlock(medals, l)}
    </li>`;
}

/**
 * Score global à part, en bandeau (résume tout le reste, un seul mode : un
 * groupe à une ligne ferait doublon avec son propre en-tête). Les autres jeux
 * sont regroupés visuellement, un bloc par jeu avec son icône en en-tête,
 * plutôt qu'une liste plate qui répéterait le nom du jeu à chaque mode
 * (« Flag Nerd · Classic », « Flag Nerd · Hard »…).
 *
 * `games` : `{ [modeId]: { score, rank?, errors?, duration?, streak?,
 * found?, avgScore?, gamesPlayed? } }` — tous les champs sauf `score` sont
 * optionnels, absents ils ne s'affichent simplement pas (le back-office n'a
 * par exemple pas de rang, calculé uniquement côté classement public).
 *
 * `dailyMedals` : `{ dailychallenge?, dailychallengehard?: {gold, silver,
 * bronze} }`, le palmarès reconstruit par `dailyMedals.js` côté serveur —
 * absent de `games` (qui ne connaît que le score du jour), donc porté à part.
 *
 * `dailyFallback` : `{ dailychallenge?, dailychallengehard?: {avgScore?,
 * gamesPlayed?, streak?} }` — progression cumulée d'un mode que `games` ne
 * porte pas parce que le joueur n'a pas joué aujourd'hui (voir `renderFallbackRow`).
 */
export function renderProfileStats(games, l, dailyMedals = {}, dailyFallback = {}) {
    const globalGroup = VISIBLE_GROUPS.find(g => g.id === "global");
    const globalEntry = globalGroup && games[globalGroup.modes[0].id];
    const globalHtml = globalEntry ? `
        <div class="player-profile-global">
            <img class="player-profile-global-icon" src="${globalGroup.icon}" alt="">
            <span class="player-profile-global-label">${l.t(globalGroup.nameKey)}</span>
            <span class="player-profile-global-score">${globalEntry.score}<span class="lb-total">/${globalGroup.modes[0].total}</span></span>
            <span class="player-profile-global-rank">${rankText(globalEntry.rank)}</span>
        </div>
    ` : "";

    const groupBlocks = VISIBLE_GROUPS.filter(g => g.id !== "global").map(group => {
        const rows = group.modes.map(mode => {
            const e = games[mode.id];
            const medals = dailyMedals?.[mode.id];
            if (e) return renderStatRow(mode, e, l, medals);
            const fallback = dailyFallback?.[mode.id];
            if (fallback || (medals && (medals.gold || medals.silver || medals.bronze))) return renderFallbackRow(mode, fallback, medals, l);
            return null;
        }).filter(Boolean);
        if (!rows.length) return "";
        return `<section class="player-profile-group">
            <div class="player-profile-group-header">
                <img class="player-profile-group-icon" src="${group.icon}" alt="">
                <span>${l.t(group.nameKey)}</span>
            </div>
            <ul class="player-profile-group-stats">${rows.join("")}</ul>
        </section>`;
    }).filter(Boolean);

    return { html: `${globalHtml}${groupBlocks.join("")}`, hasStats: !!(globalHtml || groupBlocks.length) };
}
